import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Themed date picker. A native <input type="date"> renders the OS calendar,
 * which can't be styled and looks foreign in the dark theme — so this is a
 * button + portaled calendar popover that fully matches the design system
 * (mirrors the Select component's portal/fixed-position/outside-click model).
 *
 * `value`/`onChange` speak ISO `YYYY-MM-DD` strings (same contract as the native
 * input it replaces). All date math is done on bare year/month/day integers
 * (never `toISOString`) so it is timezone-safe — picking the 4th always yields
 * "...-04", regardless of the viewer's offset. `min`/`max` (inclusive ISO bounds)
 * disable out-of-range days; string comparison is valid for `YYYY-MM-DD`.
 *
 * Navigation: month/year arrows in the day view, a clickable title that drills
 * into a month picker then a year picker, plus Clear and Today shortcuts. Today
 * is clamped into [min, max] (so when `max` is UTC-today and the viewer is a day
 * ahead locally, it still resolves to the latest selectable date).
 */

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const pad2 = (n: number) => String(n).padStart(2, "0");
/** ISO `YYYY-MM-DD` from parts (m is 0-based). */
const toIso = (y: number, m: number, d: number) => `${y}-${pad2(m + 1)}-${pad2(d)}`;

/** Parse `YYYY-MM-DD` to [year, month(0-based), day], or null if malformed. */
function parseIso(s: string): [number, number, number] | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  if (mo < 0 || mo > 11 || d < 1 || d > 31) return null;
  return [y, mo, d];
}

/** Today as parts, in the viewer's local calendar. */
function todayParts(): [number, number, number] {
  const n = new Date();
  return [n.getFullYear(), n.getMonth(), n.getDate()];
}

/** Last day-of-month (1-based) for year y, month m (0-based). */
const lastDayOfMonth = (y: number, m: number) => new Date(y, m + 1, 0).getDate();

interface DayCell {
  iso: string;
  day: number;
  inMonth: boolean;
}

/** 42 cells (6 weeks, Sunday-first) covering month (y, m), padded with siblings. */
function monthGrid(y: number, m: number): DayCell[] {
  const firstWeekday = new Date(y, m, 1).getDay(); // 0=Sun
  const cells: DayCell[] = [];
  for (let i = 0; i < 42; i++) {
    const dt = new Date(y, m, 1 - firstWeekday + i);
    cells.push({
      iso: toIso(dt.getFullYear(), dt.getMonth(), dt.getDate()),
      day: dt.getDate(),
      inMonth: dt.getMonth() === m && dt.getFullYear() === y,
    });
  }
  return cells;
}

const YEARS_PER_PAGE = 12;

type Mode = "days" | "months" | "years";

export function DatePicker({
  value,
  onChange,
  min,
  max,
  disabled,
  ariaLabel,
  placeholder = "Select a date",
}: {
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  disabled?: boolean;
  ariaLabel?: string;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>("days");
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  // Month shown in the grid + the keyboard-focused day (ISO), both seeded on open.
  const [view, setView] = useState(() => {
    const p = parseIso(value) ?? todayParts();
    return { y: p[0], m: p[1] };
  });
  const [focus, setFocus] = useState(() => value || toIso(...todayParts()));
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  const isDisabledDay = (iso: string) => (min != null && iso < min) || (max != null && iso > max);
  const clampToRange = (iso: string) => (min != null && iso < min ? min : max != null && iso > max ? max : iso);
  // A whole month / year is unreachable when its entire span is out of bounds.
  const monthDisabled = (y: number, m: number) =>
    (max != null && toIso(y, m, 1) > max) || (min != null && toIso(y, m, lastDayOfMonth(y, m)) < min);
  const yearDisabled = (y: number) =>
    (max != null && `${y}-01-01` > max) || (min != null && `${y}-12-31` < min);

  function openPicker() {
    if (disabled) return;
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 6, left: r.left });
    const seed = parseIso(value) ? value : clampToRange(toIso(...todayParts()));
    const p = parseIso(seed)!;
    setView({ y: p[0], m: p[1] });
    setFocus(seed);
    setMode("days");
    setOpen(true);
  }

  function close() {
    setOpen(false);
    btnRef.current?.focus();
  }

  function selectDay(iso: string) {
    if (isDisabledDay(iso)) return;
    onChange(iso);
    close();
  }

  function shiftMonth(delta: number) {
    setView((v) => {
      const dt = new Date(v.y, v.m + delta, 1);
      return { y: dt.getFullYear(), m: dt.getMonth() };
    });
  }

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || popRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onScroll = () => setOpen(false);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        close();
        return;
      }
      // Arrow/Enter day navigation only applies in the day grid.
      if (mode !== "days") return;
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        selectDay(focus);
        return;
      }
      const delta =
        e.key === "ArrowLeft" ? -1 :
        e.key === "ArrowRight" ? 1 :
        e.key === "ArrowUp" ? -7 :
        e.key === "ArrowDown" ? 7 : 0;
      if (delta === 0) return;
      e.preventDefault();
      const p = parseIso(focus) ?? todayParts();
      const dt = new Date(p[0], p[1], p[2] + delta);
      const next = toIso(dt.getFullYear(), dt.getMonth(), dt.getDate());
      setFocus(next);
      setView({ y: dt.getFullYear(), m: dt.getMonth() });
    };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("scroll", onScroll, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("scroll", onScroll, true);
      document.removeEventListener("keydown", onKey);
    };
    // `focus`/`mode` are read inside the key handler, so re-bind when they change.
  }, [open, focus, mode, min, max]);

  const cells = useMemo(() => monthGrid(view.y, view.m), [view.y, view.m]);
  const todayIso = toIso(...todayParts());
  const selParts = parseIso(value);
  const label = useMemo(() => {
    const p = parseIso(value);
    return p ? `${(MONTHS[p[1]] ?? "").slice(0, 3)} ${p[2]}, ${p[0]}` : placeholder;
  }, [value, placeholder]);

  // Day-view nav-arrow bounds.
  const lastOfPrev = (() => { const d = new Date(view.y, view.m, 0); return toIso(d.getFullYear(), d.getMonth(), d.getDate()); })();
  const firstOfNext = (() => { const d = new Date(view.y, view.m + 1, 1); return toIso(d.getFullYear(), d.getMonth(), d.getDate()); })();

  // Year-picker page (a block of 12), and the title/arrow state for each mode.
  const yearPageStart = Math.floor(view.y / YEARS_PER_PAGE) * YEARS_PER_PAGE;
  let title: string;
  let canPrev: boolean;
  let canNext: boolean;
  let onPrev: () => void;
  let onNext: () => void;
  if (mode === "days") {
    title = `${MONTHS[view.m]} ${view.y}`;
    canPrev = min == null || lastOfPrev >= min;
    canNext = max == null || firstOfNext <= max;
    onPrev = () => shiftMonth(-1);
    onNext = () => shiftMonth(1);
  } else if (mode === "months") {
    title = `${view.y}`;
    canPrev = min == null || `${view.y - 1}-12-31` >= min;
    canNext = max == null || `${view.y + 1}-01-01` <= max;
    onPrev = () => setView((v) => ({ ...v, y: v.y - 1 }));
    onNext = () => setView((v) => ({ ...v, y: v.y + 1 }));
  } else {
    title = `${yearPageStart}-${yearPageStart + YEARS_PER_PAGE - 1}`;
    canPrev = min == null || `${yearPageStart - 1}-12-31` >= min;
    canNext = max == null || `${yearPageStart + YEARS_PER_PAGE}-01-01` <= max;
    onPrev = () => setView((v) => ({ ...v, y: v.y - YEARS_PER_PAGE }));
    onNext = () => setView((v) => ({ ...v, y: v.y + YEARS_PER_PAGE }));
  }

  return (
    <div className="cc-datepicker">
      <button
        ref={btnRef}
        type="button"
        className={`cc-datepicker-trigger${value ? "" : " is-empty"}`}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => (open ? setOpen(false) : openPicker())}
      >
        <svg className="cc-datepicker-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <path d="M16 2v4M8 2v4M3 10h18" />
        </svg>
        <span className="cc-datepicker-value">{label}</span>
      </button>
      {open &&
        createPortal(
          <div
            ref={popRef}
            className="cc-datepicker-pop"
            role="dialog"
            aria-label={ariaLabel ?? "Choose a date"}
            style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 1000 }}
          >
            <div className="cc-datepicker-head">
              <button type="button" className="cc-datepicker-nav" disabled={!canPrev} aria-label="Previous" onClick={onPrev}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 18l-6-6 6-6" /></svg>
              </button>
              <button
                type="button"
                className="cc-datepicker-title"
                onClick={() => setMode(mode === "days" ? "months" : mode === "months" ? "years" : "days")}
                aria-label="Switch view"
              >
                {title}
              </button>
              <button type="button" className="cc-datepicker-nav" disabled={!canNext} aria-label="Next" onClick={onNext}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 18l6-6-6-6" /></svg>
              </button>
            </div>

            {mode === "days" && (
              <>
                <div className="cc-datepicker-weekdays" aria-hidden="true">
                  {WEEKDAYS.map((w) => <span key={w}>{w}</span>)}
                </div>
                <div className="cc-datepicker-grid" role="grid">
                  {cells.map((c) => {
                    const off = isDisabledDay(c.iso);
                    const klass = [
                      "cc-datepicker-day",
                      c.inMonth ? "" : "is-outside",
                      c.iso === value ? "is-selected" : "",
                      c.iso === todayIso && c.iso !== value ? "is-today" : "",
                      c.iso === focus && !off ? "is-focus" : "",
                    ].filter(Boolean).join(" ");
                    return (
                      <button
                        key={c.iso}
                        type="button"
                        className={klass}
                        role="gridcell"
                        disabled={off}
                        aria-selected={c.iso === value}
                        aria-current={c.iso === todayIso ? "date" : undefined}
                        tabIndex={-1}
                        onClick={() => selectDay(c.iso)}
                      >
                        {c.day}
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {mode === "months" && (
              <div className="cc-datepicker-panel" role="grid">
                {MONTHS.map((mName, mi) => {
                  const klass = [
                    "cc-datepicker-cell",
                    selParts && selParts[0] === view.y && selParts[1] === mi ? "is-selected" : "",
                    view.y === todayParts()[0] && mi === todayParts()[1] ? "is-current" : "",
                  ].filter(Boolean).join(" ");
                  return (
                    <button
                      key={mName}
                      type="button"
                      className={klass}
                      disabled={monthDisabled(view.y, mi)}
                      onClick={() => { setView((v) => ({ ...v, m: mi })); setMode("days"); }}
                    >
                      {mName.slice(0, 3)}
                    </button>
                  );
                })}
              </div>
            )}

            {mode === "years" && (
              <div className="cc-datepicker-panel" role="grid">
                {Array.from({ length: YEARS_PER_PAGE }, (_, i) => yearPageStart + i).map((yr) => {
                  const klass = [
                    "cc-datepicker-cell",
                    selParts && selParts[0] === yr ? "is-selected" : "",
                    yr === todayParts()[0] ? "is-current" : "",
                  ].filter(Boolean).join(" ");
                  return (
                    <button
                      key={yr}
                      type="button"
                      className={klass}
                      disabled={yearDisabled(yr)}
                      onClick={() => { setView((v) => ({ ...v, y: yr })); setMode("months"); }}
                    >
                      {yr}
                    </button>
                  );
                })}
              </div>
            )}

            <div className="cc-datepicker-foot">
              <button type="button" className="cc-datepicker-clear" disabled={!value} onClick={() => { onChange(""); close(); }}>
                Clear
              </button>
              <button type="button" className="cc-datepicker-today" disabled={min != null && max != null && min > max} onClick={() => selectDay(clampToRange(todayIso))}>
                Today
              </button>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
